import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import store from '../store'
import { onSignOutSuccess } from '../store/auth/sessionSlice'
import {
    RequirePaymentError,
    TokenError,
    CanceledError,
} from './errors'
import { COMPANY_SCOPED_ROOTS } from './queryKeys'

/**
 * React-Query-specific error handling.
 *
 * The 402 paywall event-bus dispatch and the 401 redirect already fire from
 * `api/http.request()` so every call site (RQ + imperative) sees identical
 * behavior. Here we only add the RQ-specific work: signing the user out and
 * clearing the cache when a TokenError surfaces through a query/mutation.
 *
 * General Bugfender logging also runs inside `api/http`, so no logging is
 * duplicated here.
 */
function handleGlobalError(error) {
    if (!error || error instanceof CanceledError) return
    if (error instanceof RequirePaymentError) return
    if (error instanceof TokenError) {
        try {
            store.dispatch(onSignOutSuccess())
        } catch (_) {}
        try {
            queryClient.clear()
        } catch (_) {}
        try {
            localStorage.removeItem('token')
            localStorage.removeItem('user')
        } catch (_) {}
    }
}

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: false,
            refetchOnReconnect: 'always',
            retry: (failureCount, error) => {
                if (
                    error instanceof TokenError ||
                    error instanceof RequirePaymentError ||
                    error instanceof CanceledError
                ) {
                    return false
                }
                const status = error?.status
                if (status && status >= 400 && status < 500) return false
                return failureCount < 2
            },
            retryDelay: (attempt) =>
                Math.min(1000 * Math.pow(2, attempt), 8000),
        },
        mutations: {
            retry: false,
        },
    },
    queryCache: new QueryCache({
        onError: (error) => handleGlobalError(error),
    }),
    mutationCache: new MutationCache({
        onError: (error) => handleGlobalError(error),
    }),
})

/** Use on logout: wipes all server-state caches. */
export function resetQueryCache() {
    queryClient.clear()
}

/**
 * Use on company switch. Removes (not just invalidates) all company-scoped
 * trees so stale data from the previous company can't flash on screen.
 */
export function invalidateCompanyScopedQueries() {
    COMPANY_SCOPED_ROOTS.forEach((root) =>
        queryClient.removeQueries({ queryKey: [root] })
    )
}

/** Dev-only handle for debugging in the browser console. */
if (
    process.env.NODE_ENV !== 'production' &&
    typeof window !== 'undefined'
) {
    window.__queryClient = queryClient
}
